import { Menu, MenuItem, SxProps } from '@mui/material';
import { PoiStatus, useUpdatePointOfInterestsStatusMutation } from '@/__generated__/graphql';
import { formatPoiStatus } from '@/utils';
import { SwapHoriz as UpdateStatusIcon } from '@mui/icons-material';
import { ActionButton } from './styles';
import { useState } from 'react';
import { abyssColors, white } from '@/theme/colors';
import { Theme } from '@emotion/react';
import { useSnackBarMessage } from '@/utils/useSnackBarMessage';

type Props = {
  selectedPointOfInterests: string[];
  refetchPointOfInterests: () => void;
  disabled?: boolean;
};

export const StatusChangeMenu = ({
  selectedPointOfInterests,
  refetchPointOfInterests,
  disabled,
}: Props) => {
  const [anchorElement, setAnchorElement] = useState<HTMLElement | undefined>(undefined);
  const [buttonStyle, setButtonStyle] = useState<SxProps<Theme>>({});

  const { showSnackBar } = useSnackBarMessage({ variant: 'filled' });
  const [updatePointOfInterestsStatus, { loading }] = useUpdatePointOfInterestsStatusMutation();

  const handleMenuClick = (event: React.MouseEvent<HTMLButtonElement>) => {
    setAnchorElement(event.currentTarget);
    setButtonStyle({ backgroundColor: abyssColors.primary[700], color: white });
  };

  const handleClose = () => {
    setAnchorElement(undefined);
    setButtonStyle({});
  };

  const handleChangeStatus = (status: PoiStatus) => {
    handleClose();
    updatePointOfInterestsStatus({
      variables: {
        input: { pointOfInterestIds: selectedPointOfInterests, status },
      },
    })
      .then(() => {
        refetchPointOfInterests();
        showSnackBar('POI status has been updated successfully', 'success');
      })
      .catch(() => {
        showSnackBar('Failed to update POI status', 'error');
      });
  };

  return (
    <>
      <ActionButton
        variant="outlined"
        startIcon={loading ? <></> : <UpdateStatusIcon />}
        sx={buttonStyle}
        disabled={disabled || loading}
        onClick={handleMenuClick}
      >
        {loading ? <>Updating...</> : <>Change POI Status</>}
      </ActionButton>

      <Menu
        sx={{ mt: 0.5 }}
        anchorEl={anchorElement}
        open={Boolean(anchorElement)}
        onClose={handleClose}
      >
        {Object.values(PoiStatus).map((poiStatus) => (
          <MenuItem
            sx={{ width: 180 }}
            key={poiStatus}
            value={poiStatus}
            onClick={() => handleChangeStatus(poiStatus)}
          >
            {formatPoiStatus(poiStatus)}
          </MenuItem>
        ))}
      </Menu>
    </>
  );
};
